/**
 * 通知服务
 *
 * @version 19.4.2
 * @since 19.4.2
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BaseService, IntervalLooperHelper } from '@common';
import { PagedResult } from '../shared/base.models';
import { QuestionType } from '@shared/model/itembank.models';
import { ExerciseDetailResultEnum } from '@shared/model/exercise.models';
import { isNil } from 'es-toolkit';

@Injectable({
  providedIn: 'root',
})
export class MessageApiService extends BaseService {
  /** 未读数状态（全局共享，单一轮询源），总数由服务端计算 */
  readonly unread$ = new BehaviorSubject<NoticeUnreadCountResult>({
    unread: 0,
    unreadMessage: 0,
    total: 0,
  });
  private poller: IntervalLooperHelper | null = null;
  /** 本地已读变更版本，用于丢弃变更前发出的过期轮询响应 */
  private unreadVersion = 0;

  /**
   * 启动全局未读轮询（幂等，只允许一个轮询源）
   */
  startPolling() {
    if (this.poller) return;
    this.poller = new IntervalLooperHelper(async () => {
      await this.refreshUnread();
    }, 15 * 1000).start();
  }

  /** 主动刷新全局未读摘要；本地已读变更发生后，旧请求响应不会覆盖新状态 */
  async refreshUnread() {
    const version = this.unreadVersion;
    const result = await this.getNoticeUnreadCount();
    if (version === this.unreadVersion) {
      this.unread$.next(result);
    }
    return result;
  }

  /** 系统通知列表获取成功后，按“获取即已读”清除系统通知未读 */
  consumeSystemUnread() {
    const current = this.unread$.value;
    const cleared = Math.max(0, current.unread ?? 0);
    // 即使初始 notify 尚未返回，也要让此前发出的旧请求失效。
    this.unreadVersion += 1;
    this.unread$.next({
      ...current,
      unread: 0,
      total: Math.max(0, current.total - cleared),
    });
    return cleared;
  }

  /** 具体消息 thread 获取成功后，按该叶子节点原未读数更新全局消息摘要 */
  consumeMessageUnread(count: number) {
    const current = this.unread$.value;
    const requested = Math.max(0, count || 0);
    if (requested === 0) return 0;
    const cleared = Math.min(requested, Math.max(0, current.unreadMessage || 0));
    // current 可能还是初始值 0；版本仍需递增，防止旧 notify 响应恢复已读数字。
    this.unreadVersion += 1;
    this.unread$.next({
      ...current,
      unreadMessage: Math.max(0, current.unreadMessage - cleared),
      total: Math.max(0, current.total - cleared),
    });
    return cleared;
  }

  /**
   * 获取通知未读条数
   */
  getNoticeUnreadCount() {
    return this.doGet<NoticeUnreadCountResult>(`/inbox/trainer/notify/`);
  }

  /**
   * 获取通知
   */
  getNotice(page: number) {
    return this.doGet<PagedResult<Notice>>(`/inbox/trainer/`, { page: page });
  }

  /**
   * 标为已读
   */
  onMark(idsStr: string) {
    return this.doPost(`/inbox/mark/`, { id: idsStr });
  }

  /**
   * 云平台消息聚合列表（分页）
   *
   * @param filter 选择 Unread 时后端仅返回有未读消息的任务
   */
  async getMessageThreads(
    page: number = 1,
    filter: MessageThreadFilter = MessageThreadFilter.All
  ): Promise<PagedResult<MessageThread>> {
    const result = await this.doGet<PagedResult<MessageThread> | MessageThread[]>(`/message/thread/`, {
      page: page,
      unread: filter === MessageThreadFilter.Unread ? true : undefined,
    });
    // 后端在筛选结果为空时直接返回裸数组（如「未读」无命中），不是分页结构；补成空分页，调用方无需兼容两种形状
    return Array.isArray(result) ? { results: result, count: result.length } : result;
  }

  /**
   * 云平台子话题统计
   */
  getTaskStatis(taskId: number) {
    return this.doGet<TaskStatisItem[]>(`/message/task/${taskId}/statis/`);
  }

  /**
   * 云平台子话题内消息列表（分页，page=1 为最新一页，从新往旧翻取更早历史）
   */
  getThreadMessages(
    threadId: number,
    query?: {
      paperQuestionId?: number | null;
      moduleId?: number | null;
      page?: number;
      unreplied?: boolean;
    }
  ) {
    return this.doGet<PagedResult<Message>>(`/message/thread/${threadId}/message/`, query);
  }

  /**
   * 消息的已读列表
   */
  getMessageReadStatus(msgId: number) {
    return this.doGet<MessageReadStatus[]>(`/message/${msgId}/readStatus/`);
  }

  /**
   * 消息回复
   */
  replyMessage(msgId: number, body: ReplyMessageBody) {
    return this.doPost(`/message/${msgId}/reply/`, body);
  }

  /**
   * 创建消息
   */
  createMessage(body: CreateMessageBody) {
    return this.doPost<{ id: number }>(`/message/`, body);
  }

  /**
   * 消息修改
   */
  updateMessage(msgId: number, body: UpdateMessageBody) {
    return this.doPut<{ id: number }>(`/message/${msgId}/`, body);
  }

  /**
   * 消息打标签
   */
  tagMessage(msgId: number, body: TagMessageBody) {
    return this.doPost(`/message/${msgId}/tag/`, body);
  }

  /**
   * 消息删除（批量）
   */
  deleteMessages(ids: number[]) {
    return this.doDelete(`/message/delete/`, undefined, { body: { ids } });
  }
}

/**
 * 通知未读
 */
export interface NoticeUnreadCountResult {
  /** 原系统通知未读数；没有系统通知时后端可能返回 null */
  unread: number | null;
  /** 任务消息未读数 */
  unreadMessage: number;
  /** 系统通知 + 任务消息的总未读数，由服务端计算 */
  total: number;
}

/**
 * 通知
 * extraTyep:
 *  0  新任务，未读消息，只允许一条
 *  1  消息回复
 *  2  试卷被阅
 *  3  云平台通知
 *  4  任务附件转码完成
 *  5  任务视频附件转码完成
 *  6   章节视频转码完成
 *  7   章节/知识模块 附件转码完成
 *  8   试卷附件转码完成
 *  9   视频讲解转码完成
 *  10  错题本导出,
 *  11   新的反馈
 */
export interface Notice {
  id: number;
  extraType: number;
  content: string;
  title: string; // 标题。 since 19.4.2
  createAt: Date; // 创建时间 since 19.4.2
  hasRead: boolean; // 是否已读 since 19.4.2 云平台有定义
}

/**
 * 系统消息结构化 content 场景（1557）
 *
 * 新消息的 content 为 JSON 字符串 { type, data }；旧消息仍是纯文本/HTML/旧 JSON，
 * 前端解析失败或不含 type 时走旧渲染分支，保证历史消息兼容。
 */
export enum NoticeContentType {
  Booklet = 'booklet',
  Graph = 'graph',
  Exam = 'exam',
  Transcode = 'transcode',
  Correction = 'correction',
  Edoc = 'edoc',
  /** 后端实际下发：派发工单（契约未细分，需兼容） */
  EdocAssign = 'edoc_assign',
  /** 后端实际下发：工单录入完成（契约未细分，需兼容） */
  EdocComplete = 'edoc_complete',
  Import = 'import',
}

/** 系统消息结构化 content 的成功/失败状态 */
export enum NoticeStatus {
  Success = 'success',
  Failure = 'failure',
}

/** 转码产物类型：后端下发 kind，缺失时按 duration/size 推断，兼容早期数据 */
export enum NoticeItemKind {
  Video = 'video',
  File = 'file',
}

/** 转码产物条目：视频含 cover/duration，附件含 size */
export interface NoticeMediaItem {
  kind: NoticeItemKind;
  name: string;
  url: string;
  cover?: string | null;
  duration?: number;
  size?: number;
}

/**
 * 失败时的错误信息
 *
 * message 为错误摘要；traceback 存在代表系统报错，才展示复制按钮；
 * traceback 缺失代表数据不存在/参数有误，只展示 message。
 */
export interface NoticeError {
  message?: string;
  traceback?: string;
}

/** 结构化 content 的 data 部分 */
export interface NoticeContentData {
  status: NoticeStatus;
  detail?: string;
  url?: string;
  items?: NoticeMediaItem[];
  error?: NoticeError;
}

/**
 * 消息聚合（云平台消息聚合列表）
 */
export interface MessageThread {
  taskId: number | null; // 系统消息为 null（type===0），真实任务为 number
  title: string; // 任务名称
  classroomId: number | null; // 班级ID；系统消息为 null
  classroomName: string | null; // 班级名称；系统消息为 null
  lastMessage: {
    content: string;
    mediaUrls: string[];
    firstName: string;
    tags?: { firstName?: string; tag: string }[]; // 学生只发标签时正文为空，摘要靠它展示
    messageType: MessageType; // 消息类型：0 默认 1 不懂 2 我来讲
  };
  unReadCount: number; // 新消息数
  type: number; // 任务顶级分类：作业、测验、考试，对应 1、2、3
  modifyAt: string; // 最后修改时间
}

/**
 * 消息聚合列表筛选
 *
 * Unread 由后端按「存在未读学生消息」过滤任务。
 */
export enum MessageThreadFilter {
  All = 'all',
  Unread = 'unread',
}

/**
 * 消息流筛选
 *
 * Unreplied 由后端过滤未回复消息。
 */
export enum ThreadMessageFilter {
  All = 'all',
  Unreplied = 'unreplied',
}

/**
 * 任务子话题统计（每题=一条 thread）
 */
export interface TaskStatisItem {
  createAt: string;
  moduleId: number | null; // 模块ID
  moduleName: string | null; // 模块名称；题目类型为 null
  paperQuestionId: number | null; // 题在试卷内的ID；任务/模块反馈为 null
  questionId: number | null; // 题目ID；任务/模块反馈为 null
  no: string; // 题号
  unReadCount: number; // 新未读消息数
  typeStatis: { typeId: number; typeName: string; count: number }[]; // 按消息类型统计的条数
  correctPercent: number; // 得分率
  id: number; // thread_id
}

/**
 * 「任务反馈」未读数：只统计任务级与模块级 thread（paperQuestionId 为空），不混入题目反馈
 * 由 task-analysis-chart 的 tab 角标与 fl-task-feedback 的 unreadChange 共用，保证两处口径一致
 */
export function sumTaskFeedbackUnread(statis: TaskStatisItem[]): number {
  return (statis || [])
    .filter(item => isNil(item.paperQuestionId))
    .reduce((sum, item) => sum + (item.unReadCount || 0), 0);
}

/**
 * 拉取某题消息流的查询描述（切题时全量携带：thread_id + 任务/模块/题定位）
 */
export interface MessageThreadQuery {
  threadId: number;
  taskId?: number; // 任务
  paperQuestionId?: number | null; // 题在试卷内的ID
  moduleId?: number | null; // 模块ID
}

/** 消息 thread 首屏获取成功事件；获取成功即视为当前用户已读 */
export interface MessageThreadOpenedEvent {
  query: MessageThreadQuery;
}

/** 某个任务内的叶子 thread 清除未读事件 */
export interface TaskUnreadClearedEvent {
  taskId: number;
  threadId: number;
  clearedCount: number;
}

/**
 * 消息标签
 */
export interface MessageTag {
  id: number;
  user_id: number;
  firstName: string;
  tag: string; // 标签名称
}

/**
 * 学生作答详情（题目消息返回；单选题 answer 为选项字母，主观题内容在 photos）
 */
export interface MessageExerciseDetail {
  answer: string | null;
  questionType: QuestionType; // 题目类型：0单选 1多选 2主观 3题组
  photos: string[];
  score: number; // 得分
  result: ExerciseDetailResultEnum; // 0未阅 1正确 2半对 3错误 100跳过
}

/**
 * 消息
 */
export interface Message {
  id: number;
  parentId: number | null; // 父消息ID
  userId: number;
  firstName: string;
  avatar: string;
  tags: MessageTag[];
  content: string;
  mediaUrls: string[];
  toAll: boolean;
  createAt: string;
  modifyAt: string;
  totalCount: number; // 总人数
  hasReadCount: number; // 已读人数
  isTeacher: boolean; // 是否老师发的消息
  exerciseDetail: MessageExerciseDetail[] | null; // 学生作答详情
  messageType: MessageType; // 消息类型：0 默认 1 不懂 2 我来讲
}

/**
 * 消息已读状态
 */
export interface MessageReadStatus {
  id: number;
  userId: number;
  firstName: string;
  avatar: string;
  createAt: string;
  modifyAt: string;
}

/**
 * 消息类型（服务端固定；创建消息时由发送方指定）
 *
 * 老师端创建的消息恒为 Default。
 */
export enum MessageType {
  Default = 0, // 默认
  Ask = 1, // 不懂
  Speak = 2, // 我来讲
}

/**
 * 创建消息
 */
export interface CreateMessageBody {
  taskId?: number;
  exerciseDetailId?: number | null;
  paperQuestionId?: number | null;
  tagId?: number;
  content: string;
  mediaUrls: string[];
  type: MessageType;
  moduleId?: number | null; // 模块ID
}

/**
 * 消息回复
 */
export interface ReplyMessageBody {
  content: string;
  mediaUrls?: string[];
  toAll?: boolean;
}

/**
 * 消息修改
 */
export interface UpdateMessageBody {
  content?: string;
  mediaUrls?: string[];
}

/**
 * 消息打标签
 */
export interface TagMessageBody {
  tagId: number;
}

/**
 * 消息标签枚举（服务端固定）
 */
export enum MessageTagType {
  Understood = 1, // 听懂了
  Thanks = 2, // 感谢
}

/**
 * 消息标签 id → 名称映射
 */
export const MessageTagTypeMap: Record<number, string> = {
  [MessageTagType.Understood]: '听懂了',
  [MessageTagType.Thanks]: '感谢',
};
